import axios from 'axios';
import type { InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { Message } from '@arco-design/web-vue';
import type { DataResponse } from '@/api/types';

if (import.meta.env.VITE_APP_BASE_URL) {
  axios.defaults.baseURL = import.meta.env.VITE_APP_BASE_URL;
}

axios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // 从 localStorage 获取 token
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      // 添加 Bearer token
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 删除原来的响应拦截器，改为使用包装函数处理响应
axios.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // 处理认证错误，跳转到登录页面
    if (error.response && error.response.status === 401) {
      // 清除本地存储的token
      localStorage.removeItem('token');

      // 显示认证失败消息
      Message.error({
        content: '认证失败，请重新登录',
        duration: 5000,
        closable: true,
        position: 'top'
      });

      // 获取当前路径，用于登录后重定向
      const currentPath = window.location.pathname;

      // 如果当前不在登录页，则跳转到登录页
      if (currentPath !== '/login') {
        // 延迟跳转，让用户有时间看到错误消息
        setTimeout(() => {
          window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
        }, 1500);
      }

      return Promise.reject(error);
    }
    if (error.code === 'ERR_NETWORK') {
      Message.error({
        content: '网络异常，请检查网络连接',
        duration: 5000,
      });
      // window.location.href = '/error';
    } else {
      Message.error({
        content: error.message || '请求失败',
        duration: 5000,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * 通用请求函数，处理 DataResponse 结构并返回数据
 * @param axiosPromise axios 请求 Promise
 * @returns 处理后的数据
 */
export async function request<T>(axiosPromise: Promise<AxiosResponse<DataResponse<T>>>): Promise<T> {
  const response = await axiosPromise;
  const res = response.data;
  if (res.code !== 200) {
    Message.error({
      content: res.message || 'Error',
      duration: 5000,
    });

    if (res.code === 401) {
      Message.error({
        content: '认证失败，请重新登录',
        duration: 50000,
        closable: true,
        position: 'top'
      });
      // token expired, redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    throw new Error(res.message || 'Error');
  }

  return res.data;
}

/**
 * 处理带分页信息的请求
 * @param axiosPromise axios 请求 Promise
 * @returns 包含数据和分页信息的对象
 */
export async function requestWithPagination<T>(axiosPromise: Promise<AxiosResponse<DataResponse<T>>>): Promise<{ data: T, total: number, skip?: number, limit?: number }> {
  const response = await axiosPromise;
  const res = response.data;

  if (res.code !== 200) {
    Message.error({
      content: res.message || 'Error',
      duration: 5000,
    });

    if (res.code === 401) {
      Message.error({
        content: '认证失败，请重新登录',
        duration: 50000,
        closable: true,
        position: 'top'
      });
      // token expired, redirect to login
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    throw new Error(res.message || 'Error');
  }

  return {
    data: res.data,
    total: res.total || 0,
    skip: res.skip,
    limit: res.limit
  };
}

export default axios;
